import React, { useEffect, useMemo, useRef, useState } from "react";
import { PaymentGateway, SettingsValue } from "types/payments";
import CardDetailsTabs from "pages/payments/CardDetailsTab";
import { useAppSelector, useAppDispatch } from "store/hooks";
import { getBankLogo, getWalletIcon } from "utils/HelperFunctions";
import { resetConvFee } from "store/slices/paymentsSlice";
import type {
  SavedPaymentListProps,
  FormattedPaymentMethodTypes,
  CountryWithStates,
} from "types/paymentMethodComponent";
import { RootState } from "store/store";
import {
  ConvenienceFeeDisplay,
  ShowMoreButton,
  PaymentMethodLoadingSkeletons,
} from "components/paymentMethod/PaymentMethodComponents";
import { PaymentMethodTypes } from "types/paymentMethods";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { countryAndState } from "store/slices/documentsSlice";
import { useUserData } from "store/slices/authSlice";
import { useTestAttrsFactory, useTid } from "utils/testid";
import { getUsableSavedPaymentMethods } from "utils/paymentMethodAvailability";
import { hasValidPaymentPrincipal } from "utils/paymentFee";

const SavedPaymentMethodList = ({
  cardList,
  bankList,
  walletList,
  showAllWallets,
  setShowAllWallets,
  isDataLoading = false,
  showAllCards,
  handleCardOrBankSelect,
  setShowAllCards,
  showAllBanks,
  setShowAllBanks,
  data,
  isPaymentLink = true,
  setShowPreview = () => {},
  paymentMethodList,
  custom_conv = "on",
  cppId = null,
  amount,
}: SavedPaymentListProps): JSX.Element => {
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  const dispatch = useAppDispatch();
  const { conFee, isFeeLoading, paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );
  const userData = useUserData();
  const portalSettings = usePortalSettingsData();

  const [countriesWithStates, setCountriesWithStates] = useState<
    CountryWithStates[]
  >([]);

  const normalizedCppId = useMemo(() => {
    if (cppId === null || cppId === undefined || cppId === "") {
      return null;
    }

    const parsedCppId = Number(cppId);
    return Number.isNaN(parsedCppId) ? null : parsedCppId;
  }, [cppId]);

  const prioritizeMethods = useMemo(
    () =>
      (paymentMethods: FormattedPaymentMethodTypes[] = []) => {
        if (paymentMethods.length === 0) {
          return paymentMethods;
        }

        return paymentMethods
          .map((paymentMethod, index) => ({ paymentMethod, index }))
          .sort((a, b) => {
            const aIsOldMethod =
              normalizedCppId !== null &&
              (a.paymentMethod.id === normalizedCppId ||
                a.paymentMethod.formatted?.id === normalizedCppId);
            const bIsOldMethod =
              normalizedCppId !== null &&
              (b.paymentMethod.id === normalizedCppId ||
                b.paymentMethod.formatted?.id === normalizedCppId);
            const aIsDefault = a.paymentMethod.default_profile === "Y";
            const bIsDefault = b.paymentMethod.default_profile === "Y";

            if (aIsOldMethod !== bIsOldMethod) {
              return aIsOldMethod ? -1 : 1;
            }

            if (aIsDefault !== bIsDefault) {
              return aIsDefault ? -1 : 1;
            }

            return a.index - b.index;
          })
          .map(({ paymentMethod }) => paymentMethod);
      },
    [normalizedCppId]
  );

  const usableSavedPaymentMethods = useMemo(
    () =>
      getUsableSavedPaymentMethods({
        cardList,
        bankList,
        walletList,
        paymentMethodList,
        paymentPermission,
      }),
    [cardList, bankList, walletList, paymentMethodList, paymentPermission]
  );

  const prioritizedCardList = useMemo(
    () => prioritizeMethods(usableSavedPaymentMethods.cards),
    [usableSavedPaymentMethods.cards, prioritizeMethods]
  );

  const prioritizedBankList = useMemo(
    () => prioritizeMethods(usableSavedPaymentMethods.banks),
    [usableSavedPaymentMethods.banks, prioritizeMethods]
  );

  const prioritizedWalletList = useMemo(
    () => prioritizeMethods(usableSavedPaymentMethods.wallets),
    [usableSavedPaymentMethods.wallets, prioritizeMethods]
  );

  useEffect(() => {
    dispatch(resetConvFee());
  }, []);

  // The step this list lives on unmounts when the customer goes back, so on
  // remount the default would silently replace whatever they had picked.
  // Re-selecting their own choice keeps the fee and the saved method in step.
  const hadSelectionOnMount = useRef(
    Boolean(
      data?.selectedCard?.formatted?.id ??
      data?.selectedCard?.id ??
      data?.selectedBank?.formatted?.id ??
      data?.selectedBank?.id
    )
  );

  // The resetConvFee() above clears the fee on every mount, and the
  // default-selection effect below deliberately bails out when the customer
  // already had a method chosen — so nothing recalculated the fee for that
  // surviving selection. The method stayed visibly selected while the fee line
  // read "$0.00" until they picked something again. Re-selecting their own
  // choice puts the fee back in step with it.
  useEffect(() => {
    if (!hadSelectionOnMount.current) return;

    const cardId = data?.selectedCard?.formatted?.id ?? data?.selectedCard?.id;
    const bankId = data?.selectedBank?.formatted?.id ?? data?.selectedBank?.id;
    const savedCardIsUsable = prioritizedCardList.some(
      (method) => method.id === cardId || method.formatted?.id === cardId
    );
    const savedBankIsUsable = prioritizedBankList.some(
      (method) => method.id === bankId || method.formatted?.id === bankId
    );

    if (cardId && savedCardIsUsable) {
      handleCardOrBankSelect("Card", cardId as number, true);
    } else if (bankId && savedBankIsUsable) {
      handleCardOrBankSelect("Bank", bankId as number, true);
    } else {
      hadSelectionOnMount.current = false;
    }
  }, [prioritizedCardList, prioritizedBankList]);

  useEffect(() => {
    if (hadSelectionOnMount.current) return;

    // If normalizedCppId is null, select default method
    if (normalizedCppId === null) {
      console.log("normalizedCppId ====== null");
      let defaultCard: PaymentMethodTypes[] = [];
      let defaultBank: PaymentMethodTypes[] = [];

      if (prioritizedCardList.length > 0) {
        defaultCard = prioritizedCardList.filter(
          (obj: PaymentMethodTypes) => obj.default_profile === "Y"
        );
      }

      if (
        prioritizedBankList.length > 0 &&
        paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE
      ) {
        defaultBank = prioritizedBankList.filter(
          (obj: PaymentMethodTypes) => obj.default_profile === "Y"
        );
      }

      if (defaultCard.length > 0) {
        handleCardOrBankSelect("Card", defaultCard[0]?.id as number, true);
      } else if (
        defaultBank.length > 0 &&
        paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE
      ) {
        handleCardOrBankSelect("Bank", defaultBank[0]?.id as number, true);
      }
      return;
    }

    // Try to match and select by cppId
    const matchedCard = prioritizedCardList.find(
      (obj: FormattedPaymentMethodTypes) =>
        obj.id === normalizedCppId || obj.formatted?.id === normalizedCppId
    );
    const matchedBank = prioritizedBankList.find(
      (obj: FormattedPaymentMethodTypes) =>
        obj.id === normalizedCppId || obj.formatted?.id === normalizedCppId
    );

    if (matchedCard) {
      handleCardOrBankSelect("Card", matchedCard.id as number, true);
      return;
    }

    if (
      matchedBank &&
      paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE
    ) {
      handleCardOrBankSelect("Bank", matchedBank.id as number, true);
      return;
    }

    // If cppId doesn't match anything, fall back to default
    let defaultCard: PaymentMethodTypes[] = [];
    let defaultBank: PaymentMethodTypes[] = [];

    if (prioritizedCardList.length > 0) {
      defaultCard = prioritizedCardList.filter(
        (obj: PaymentMethodTypes) => obj.default_profile === "Y"
      );
    }

    if (
      prioritizedBankList.length > 0 &&
      paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE
    ) {
      defaultBank = prioritizedBankList.filter(
        (obj: PaymentMethodTypes) => obj.default_profile === "Y"
      );
    }

    if (defaultCard.length > 0) {
      handleCardOrBankSelect("Card", defaultCard[0]?.id as number, true);
    } else if (
      defaultBank.length > 0 &&
      paymentPermission?.ach_gateway_name?.name !== PaymentGateway.STRIPE
    ) {
      handleCardOrBankSelect("Bank", defaultBank[0]?.id as number, true);
    }
  }, [
    prioritizedCardList,
    prioritizedBankList,
    normalizedCppId,
    paymentPermission,
  ]);

  useEffect(() => {
    if (!normalizedCppId || prioritizedWalletList.length === 0) {
      return;
    }

    const matchedWallet = prioritizedWalletList.find(
      (wallet: FormattedPaymentMethodTypes) =>
        wallet.id === normalizedCppId ||
        wallet.formatted?.id === normalizedCppId
    );

    if (matchedWallet) {
      handleCardOrBankSelect("Wallets", matchedWallet.id as number, true);
    }
  }, [prioritizedWalletList, normalizedCppId]);

  const showConvFee = useMemo(() => {
    if (amount !== undefined && !hasValidPaymentPrincipal(amount)) {
      return false;
    }

    if (
      paymentPermission?.cc_gateway_name?.name === PaymentGateway.STAX &&
      data?.selectedCard &&
      Object.keys(data?.selectedCard)?.length > 0
    ) {
      return true;
    } else if (
      paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX &&
      data?.selectedBank &&
      Object.keys(data?.selectedBank)?.length > 0
    ) {
      return true;
    }

    if (portalSettings?.convenience_fee_opt === SettingsValue.YES) {
      return true;
    } else if (
      portalSettings?.convenience_fee_opt === SettingsValue.CUSTOM &&
      custom_conv === "on"
    ) {
      return true;
    } else if (
      portalSettings?.convenience_fee_opt === SettingsValue.CUSTOM &&
      (custom_conv === "off" || custom_conv === "")
    ) {
      return false;
    } else if (portalSettings?.convenience_fee_opt === SettingsValue.NO) {
      return false;
    }
    return conFee > 0;
  }, [amount, portalSettings, custom_conv, conFee, paymentPermission, data]);

  useEffect(() => {
    const loadCountries = async (): Promise<void> => {
      try {
        const res: any = await dispatch(countryAndState()).unwrap();
        const data = (res?.body?.data || []) as CountryWithStates[];
        setCountriesWithStates(data);
      } catch (e) {
        console.error("Error loading countries and states:", e);
      }
    };
    loadCountries();
  }, []);

  const getMethodKey = (method: FormattedPaymentMethodTypes) =>
    method?.formatted?.id ||
    method?.id ||
    method?.cc_token ||
    method?.paypalid ||
    method?.account_email ||
    "unknown";

  return (
    <>
      {prioritizedCardList?.length > 0 &&
        paymentMethodList?.some((item: any) => item?.name === "Card") && (
          <div
            className="animate__animated animate__fadeIn"
            {...testAttrs("saved-cards-section", "Saved cards")}
          >
            <div
              className="text-sm leading-[18px] text-iconBright mb-2"
              {...testAttrs("saved-cards-heading")}
            >
              Cards
            </div>
            {isDataLoading ? (
              <PaymentMethodLoadingSkeletons count={3} />
            ) : (
              <div className="flex flex-col gap-y-2 pb-4 border-b border-b-gray">
                {prioritizedCardList
                  ?.slice(0, showAllCards)
                  .map((itm: FormattedPaymentMethodTypes) => (
                    <div
                      key={getMethodKey(itm)}
                      data-testid={tid(`saved-card-${getMethodKey(itm)}`)}
                      aria-label={`${itm?.formatted?.name || "Saved card"} payment method`}
                      className={
                        !itm?.available ||
                        itm?.formatted?.desc.includes("Expired")
                          ? "cursor-default"
                          : `cursor-pointer`
                      }
                      onClick={() => {
                        if (
                          !itm?.available ||
                          !itm.formatted ||
                          !itm.formatted.id
                        )
                          return;
                        if (!itm?.formatted?.desc.includes("Expired")) {
                          handleCardOrBankSelect(
                            "Card",
                            itm.formatted.id as number
                          );
                        }
                      }}
                    >
                      <CardDetailsTabs
                        name={itm?.formatted?.name || ""}
                        desc={itm?.formatted?.desc || ""}
                        cardType={itm?.cc_type}
                        isSelected={
                          itm?.formatted?.id ===
                          data?.selectedCard?.formatted?.id
                        }
                        type="card"
                        cardInfo={itm}
                        handleSelect={handleCardOrBankSelect}
                        defaultValue={itm?.default_profile}
                        available={itm?.available}
                        paymentMethod={itm?.payment_method || ""}
                        paypalid={itm?.paypalid || ""}
                        countriesWithStates={countriesWithStates}
                        accountEmail={itm?.account_email || ""}
                      />
                    </div>
                  ))}
                {data?.selectedCard &&
                  Object.keys(data?.selectedCard)?.length > 0 &&
                  showConvFee &&
                  portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
                    <ConvenienceFeeDisplay
                      conFee={conFee}
                      isFeeLoading={isFeeLoading}
                      isPaymentLink={isPaymentLink}
                      setShowPreview={setShowPreview}
                      isStaxGateway={
                        paymentPermission?.cc_gateway_name?.name ===
                        PaymentGateway.STAX
                      }
                    />
                  )}
                {prioritizedCardList?.length > 3 && (
                  <ShowMoreButton
                    currentCount={showAllCards}
                    totalCount={prioritizedCardList?.length}
                    onToggle={() => {
                      if (showAllCards > prioritizedCardList?.length) {
                        setShowAllCards(3);
                      } else {
                        setShowAllCards((state: number) => state + 4);
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}

      {prioritizedBankList?.length > 0 &&
        paymentMethodList?.some((item: any) => item?.name === "Bank") && (
          <div
            className="animate__animated animate__fadeIn"
            {...testAttrs("saved-banks-section", "Saved banks")}
          >
            <div
              className="text-sm leading-[18px] text-iconBright mt-4 mb-2"
              {...testAttrs("saved-banks-heading")}
            >
              Banks
            </div>
            {isDataLoading ? (
              <PaymentMethodLoadingSkeletons count={3} />
            ) : (
              <div className="flex flex-col gap-y-2 pb-4 border-b border-b-gray">
                {prioritizedBankList
                  ?.slice(0, showAllBanks)
                  .map((itm: FormattedPaymentMethodTypes) => (
                    <div
                      key={getMethodKey(itm)}
                      data-testid={tid(`saved-bank-${getMethodKey(itm)}`)}
                      aria-label={`${itm?.formatted?.name || "Saved bank"} payment method`}
                      className={
                        itm?.available && itm?.is_connected === "Y"
                          ? `cursor-pointer`
                          : "cursor-default"
                      }
                      onClick={() => {
                        if (
                          !itm?.available ||
                          !itm.formatted ||
                          !itm.formatted.id ||
                          itm?.is_connected === "N"
                        )
                          return;
                        handleCardOrBankSelect(
                          "Bank",
                          itm.formatted.id as number
                        );
                      }}
                    >
                      <CardDetailsTabs
                        icon={getBankLogo({
                          bankName: itm?.bank_name,
                          type: "small",
                        })}
                        name={itm?.formatted?.name || ""}
                        desc={itm?.formatted?.desc || ""}
                        isSelected={
                          itm?.formatted?.id ===
                          data?.selectedBank?.formatted?.id
                        }
                        type="bank"
                        defaultValue={itm?.default_profile}
                        available={itm?.available}
                        paymentMethod={itm?.payment_method || ""}
                        paypalid={itm?.paypalid || ""}
                        accountEmail={itm?.account_email || ""}
                        isLinked={itm?.is_connected === "Y"}
                      />
                    </div>
                  ))}
                {data?.selectedBank &&
                  Object.keys(data?.selectedBank)?.length > 0 &&
                  showConvFee &&
                  portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
                    <ConvenienceFeeDisplay
                      conFee={conFee}
                      isFeeLoading={isFeeLoading}
                      isPaymentLink={isPaymentLink}
                      setShowPreview={setShowPreview}
                      isStaxGateway={
                        paymentPermission?.ach_gateway_name?.name ===
                        PaymentGateway.STAX
                      }
                    />
                  )}
                {prioritizedBankList?.length > 3 && (
                  <ShowMoreButton
                    currentCount={showAllBanks}
                    totalCount={prioritizedBankList?.length}
                    onToggle={() => {
                      if (showAllBanks > prioritizedBankList?.length) {
                        setShowAllBanks(3);
                      } else {
                        setShowAllBanks((state: number) => state + 4);
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}

      {prioritizedWalletList?.length > 0 && (
        <div
          className="animate__animated animate__fadeIn"
          {...testAttrs("saved-wallets-section", "Saved digital wallets")}
        >
          <div
            className="text-sm leading-[18px] text-iconBright mt-4 mb-2"
            {...testAttrs("saved-wallets-heading")}
          >
            Digital Wallets
          </div>
          {isDataLoading ? (
            <PaymentMethodLoadingSkeletons count={3} />
          ) : (
            <div className="flex flex-col gap-y-2 pb-4">
              {prioritizedWalletList
                ?.slice(0, showAllWallets)
                .map((itm: FormattedPaymentMethodTypes) => (
                  <div
                    key={getMethodKey(itm)}
                    data-testid={tid(`saved-wallet-${getMethodKey(itm)}`)}
                    aria-label={`${itm?.formatted?.name || "Saved wallet"} payment method`}
                    className={
                      itm?.available ? `cursor-pointer` : "cursor-default"
                    }
                    onClick={() => {
                      if (
                        !itm?.available ||
                        !itm.formatted ||
                        !itm.formatted.id
                      )
                        return;
                      handleCardOrBankSelect(
                        "Wallets",
                        itm?.formatted?.id as number
                      );
                    }}
                  >
                    <CardDetailsTabs
                      icon={getWalletIcon({
                        walletName: itm?.formatted?.name,
                        size: "small",
                      })}
                      name={itm?.formatted?.name || ""}
                      desc={itm?.formatted?.desc || ""}
                      isSelected={
                        itm?.formatted?.id ===
                        data?.selectedWallet?.formatted?.id
                      }
                      type="Wallets"
                      cardInfo={itm}
                      defaultValue={itm?.default_profile}
                      available={itm?.available}
                      paymentMethod={itm?.payment_method || ""}
                      paypalid={itm?.paypalid || ""}
                      accountEmail={itm?.account_email || ""}
                    />
                  </div>
                ))}
              {data?.selectedWallet &&
                Object.keys(data?.selectedWallet)?.length > 0 &&
                showConvFee &&
                portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
                  <ConvenienceFeeDisplay
                    conFee={conFee}
                    isFeeLoading={isFeeLoading}
                    isPaymentLink={isPaymentLink}
                    setShowPreview={setShowPreview}
                  />
                )}
              {prioritizedWalletList?.length > 3 && (
                <ShowMoreButton
                  currentCount={showAllWallets}
                  totalCount={prioritizedWalletList?.length}
                  onToggle={() => {
                    if (showAllWallets > prioritizedWalletList?.length) {
                      setShowAllWallets(2);
                    } else {
                      setShowAllWallets((state: number) => state + 2);
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SavedPaymentMethodList;
